import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import type { Phone } from '../phone/phoneTypes';
import type { WhatsAppNumber } from './whatsAppMessageTypes';

/**
 * WHATSAPP NUMBER FIELDS — le due select dell'invio WhatsApp: il mittente (numero
 * business) e il numero del contatto. Le usano il modale d'invio e la pagina
 * WhatsApp (FILO-37); il resto delle props va al `TextField`.
 */
type SelectProps = Omit<TextFieldProps, 'select' | 'value' | 'onChange' | 'children'> & {
  value: string;
  onChange: (value: string) => void;
};

/**
 * Il mittente. Si vede sempre, anche con un numero solo, che è già scelto: chi
 * scrive vede da quale numero parte il messaggio. A registro vuoto non c'è.
 * `labelOnly`: solo l'etichetta (Commerciale, Assistenza…), senza il numero.
 */
export function WhatsAppSenderSelect({
  numbers,
  value,
  onChange,
  preferredUnavailable,
  labelOnly,
  ...rest
}: SelectProps & { numbers: WhatsAppNumber[]; preferredUnavailable?: boolean; labelOnly?: boolean }) {
  const { t } = useTranslation();

  if (numbers.length === 0) return null;

  return (
    <TextField
      {...rest}
      select
      label={t('whatsapp.sender')}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      helperText={preferredUnavailable ? t('whatsapp.senderUnavailable') : rest.helperText}
    >
      {numbers.map((n) => (
        <MenuItem key={n.whatsAppNumberId} value={n.phoneNumberId}>
          {labelOnly ? n.label : `${n.label} (${n.phoneNumber})`}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** Il numero del contatto a cui si scrive, col principale segnato. */
export function WhatsAppPhoneSelect({
  phones,
  value,
  onChange,
  ...rest
}: SelectProps & { phones: Phone[] }) {
  const { t } = useTranslation();

  return (
    <TextField
      {...rest}
      select
      label={t('whatsapp.phoneNumber')}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {phones.map((p) => (
        <MenuItem key={p.phoneId} value={p.number}>
          {p.number}
          {p.primary ? ` (${t('whatsapp.primary')})` : ''}
        </MenuItem>
      ))}
    </TextField>
  );
}
